import React, { useState, useMemo } from 'react';
import { 
    Box, Typography, Container, Paper,    AlertTitle,
    Grid, Button, Card, CardContent, Divider, 
    Alert, Chip, useTheme, LinearProgress,
    FormControl, InputLabel, Select, MenuItem,
    TextField, Dialog, DialogTitle, DialogContent, 
    DialogActions, Tooltip
} from '@mui/material';
import { 
    CloudUpload as CloudUploadIcon, 
    Restore as RestoreIcon, 
    Settings as SettingsIcon, 
    Schedule as ScheduleIcon, 
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Storage as StorageIcon,
    Replay as ReplayIcon,
    Error as ErrorIcon // Ensure ErrorIcon is imported
} from '@mui/icons-material';
import moment from 'moment';

// --- MOCK DATA ---

const lastBackupTime = moment().subtract(5, 'hours').toISOString();
const nextBackupTime = moment().add(19, 'hours').toISOString();

const mockBackupHistory = [
    { id: 1, type: 'Full Database', timestamp: moment().subtract(1, 'day').toISOString(), size: '15.2 GB', status: 'Completed', retention: '30 days' },
    { id: 2, type: 'Incremental Filesystem', timestamp: moment().subtract(12, 'hours').toISOString(), size: '488 MB', status: 'Completed', retention: '7 days' },
    { id: 3, type: 'Full Database', timestamp: moment().subtract(2, 'days').toISOString(), size: '15.1 GB', status: 'Completed', retention: '30 days' },
    { id: 4, type: 'Full Filesystem', timestamp: moment().subtract(5, 'days').toISOString(), size: '1.2 TB', status: 'Completed', retention: '90 days' },
];

const mockSchedules = [
    { id: 'db_full', name: 'Database Full Backup', frequency: 'Daily (02:00 UTC)', retention: '30 Days', nextRun: nextBackupTime },
    { id: 'fs_inc', name: 'Filesystem Incremental', frequency: 'Hourly', retention: '7 Days', nextRun: moment().add(30, 'minutes').toISOString() },
    { id: 'config_full', name: 'Config Snapshot', frequency: 'Weekly (Sun 03:00 UTC)', retention: '90 Days', nextRun: moment().endOf('week').add(3, 'hours').toISOString() },
];

// --- HELPER COMPONENTS ---

const BackupStatusChip = ({ status, theme }) => {
    let color;
    let icon;
    switch (status) {
        case 'Completed':
            color = theme.palette.success.main;
            icon = <CheckCircleIcon style={{ fontSize: 16 }} />;
            break;
        case 'Running':
            color = theme.palette.info.main;
            icon = <HourglassEmptyIcon style={{ fontSize: 16 }} />;
            break;
        case 'Failed':
            color = theme.palette.error.main;
            icon = <ErrorIcon style={{ fontSize: 16 }} />;
            break;
        default:
            color = theme.palette.grey[500];
            icon = <StorageIcon style={{ fontSize: 16 }} />;
    }
    return (
        <Chip
            label={status}
            size="small"
            icon={icon}
            sx={{
                backgroundColor: color,
                color: 'white',
                fontWeight: 'bold',
                minWidth: 100,
            }}
        />
    );
};

// --- RESTORE DIALOG COMPONENT ---

const RestoreDialog = ({ open, handleClose }) => {
    const [selectedBackup, setSelectedBackup] = useState('');
    const [targetSystem, setTargetSystem] = useState('Production'); // Default
    const theme = useTheme();

    const backups = useMemo(() => mockBackupHistory.filter(b => b.status === 'Completed'), []);

    const handleRestore = () => {
        if (!selectedBackup) {
            alert('Please select a backup point.');
            return;
        }
        console.log(`Restoring backup ${selectedBackup} to ${targetSystem}`);
        alert(`Initiating restore of ${selectedBackup} to ${targetSystem}... Check logs for status.`);
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 700 }}>
                <ReplayIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                CRITICAL: Initiate System Restore
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="error" sx={{ mb: 3 }}>
                    <AlertTitle>WARNING: Data Overwrite</AlertTitle>
                    Restoring will overwrite all current data on the target system with data from the selected backup point. This action is **irreversible**.
                </Alert>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="backup-select-label">Select Restore Point</InputLabel>
                    <Select
                        labelId="backup-select-label"
                        value={selectedBackup}
                        label="Select Restore Point"
                        onChange={(e) => setSelectedBackup(e.target.value)}
                    >
                        {backups.map((backup) => (
                            <MenuItem key={backup.id} value={backup.id}>
                                **{backup.type}** ({backup.size}) from {moment(backup.timestamp).format('YYYY-MM-DD HH:mm')} UTC
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label="Target Environment"
                    value={targetSystem}
                    onChange={(e) => setTargetSystem(e.target.value)}
                    helperText="Specify the environment (e.g., Production, Staging, QA) for the restore."
                    sx={{ mb: 3 }}
                />
                
                <TextField
                    fullWidth
                    label="Reason for Restore"
                    multiline
                    rows={2}
                    required
                    placeholder="E.g., Catastrophic data loss, Security breach rollback."
                />

            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary" variant="outlined">Cancel</Button>
                <Button onClick={handleRestore} color="error" variant="contained" startIcon={<RestoreIcon />} disabled={!selectedBackup}>
                    Confirm and Restore
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// --- MAIN COMPONENT ---
const BackupRecoveryControl = () => {
    const theme = useTheme();
    const [isBackupRunning, setIsBackupRunning] = useState(false);
    const [openRestoreDialog, setOpenRestoreDialog] = useState(false);

    const handleManualBackup = () => {
        setIsBackupRunning(true);
        // Simulate API call delay
        setTimeout(() => {
            setIsBackupRunning(false);
            alert('Manual Full Backup completed successfully.');
            // In a real app, you would fetch the new mockHistory here.
        }, 3000); 
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                }}
            >
                <CloudUploadIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.success.main }} />
                Backup & Disaster Recovery Control
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Monitor backup status, manage schedules, and control data restore operations.
            </Typography>

            <Grid container spacing={4}>

                {/* --- Section 1: Real-time Status --- */}
                <Grid item xs={12} lg={6}>
                    <Card elevation={4} sx={{ height: '100%', borderTop: `5px solid ${theme.palette.success.main}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <StorageIcon color="success" sx={{ mr: 1 }} />
                                <Typography variant="h5" fontWeight={700} color="success.dark">Current Backup Status</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Typography variant="body1" sx={{ mb: 1 }}>
                                **Last Successful Backup:** {moment(lastBackupTime).format('YYYY-MM-DD HH:mm:ss')} UTC
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                **Next Scheduled Backup:** {moment(nextBackupTime).fromNow()} ({moment(nextBackupTime).format('HH:mm')} UTC)
                            </Typography>

                            <Box sx={{ my: 3 }}>
                                {isBackupRunning ? (
                                    <Box>
                                        <Typography color="info.main" sx={{ mb: 1, fontWeight: 600 }}>
                                            Backup In Progress... (95% Complete)
                                        </Typography>
                                        <LinearProgress color="info" variant="determinate" value={95} sx={{ height: 10, borderRadius: 5 }} />
                                    </Box>
                                ) : (
                                    <Alert severity="success">
                                        All primary systems are currently backed up and within RPO targets.
                                    </Alert>
                                )}
                            </Box>

                            <Button 
                                variant="contained" 
                                color="secondary" 
                                startIcon={<CloudUploadIcon />}
                                onClick={handleManualBackup}
                                disabled={isBackupRunning}
                                sx={{ mr: 2 }}
                            >
                                {isBackupRunning ? 'Running...' : 'Initiate Manual Full Backup'}
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                startIcon={<RestoreIcon />}
                                onClick={() => setOpenRestoreDialog(true)}
                            >
                                Restore Data
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {/* --- Section 2: Backup Schedules --- */}
                <Grid item xs={12} lg={6}>
                    <Card elevation={4} sx={{ height: '100%', borderTop: `5px solid ${theme.palette.info.main}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <ScheduleIcon color="info" sx={{ mr: 1 }} />
                                    <Typography variant="h5" fontWeight={700} color="info.dark">Scheduled Jobs</Typography>
                                </Box>
                                <Tooltip title="Edit Backup Schedules">
                                    <Button size="small" startIcon={<SettingsIcon />} variant="outlined">Edit</Button>
                                </Tooltip>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            
                            {mockSchedules.map((schedule, index) => (
                                <Box key={schedule.id} sx={{ mb: 2, p: 1, borderLeft: `3px solid ${theme.palette.info.light}`, bgcolor: theme.palette.grey[50] }}>
                                    <Typography variant="body1" fontWeight={600}>{schedule.name}</Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}><Typography variant="body2" color="textSecondary">Frequency: {schedule.frequency}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="body2" color="textSecondary">Retention: {schedule.retention}</Typography></Grid>
                                        <Grid item xs={12}><Typography variant="body2" color="primary.dark">Next: **{moment(schedule.nextRun).calendar()}**</Typography></Grid>
                                    </Grid>
                                </Box>
                            ))}

                        </CardContent>
                    </Card>
                </Grid>

                {/* --- Section 3: Backup History Table (Simplified) --- */}
                <Grid item xs={12}>
                    <Paper elevation={4} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Recent Backup History</Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2} sx={{ fontWeight: 700, borderBottom: `1px solid ${theme.palette.divider}`, pb: 1, mb: 1 }}>
                            <Grid item xs={3}>Type</Grid>
                            <Grid item xs={3}>Timestamp (UTC)</Grid>
                            <Grid item xs={2}>Size</Grid>
                            <Grid item xs={2}>Retention</Grid>
                            <Grid item xs={2}>Status</Grid>
                        </Grid>

                        {mockBackupHistory.map((backup) => (
                            <Grid container spacing={2} key={backup.id} sx={{ py: 1, '&:hover': { bgcolor: theme.palette.action.hover } }}>
                                <Grid item xs={3}>
                                    <Typography variant="body2">{backup.type}</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="body2">{moment(backup.timestamp).format('YYYY-MM-DD HH:mm')}</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body2">{backup.size}</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body2">{backup.retention}</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <BackupStatusChip status={backup.status} theme={theme} />
                                </Grid>
                            </Grid>
                        ))}
                        
                    </Paper>
                </Grid>
            </Grid>

            {/* Restore Confirmation/Selection Dialog */}
            <RestoreDialog 
                open={openRestoreDialog} 
                handleClose={() => setOpenRestoreDialog(false)} 
            />
        </Container>
    );
};

export default BackupRecoveryControl;